import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  labelById,
  parseAmountInput,
  startOfDay,
  toggleFilterListValue,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";

export const EMPTY_PIX_CHARGES_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  statuses: [],
  types: [],
  txid: "",
  chargeId: "",
  searchQuery: "",
};

export const PIX_CHARGE_STATUSES = [
  { id: "waiting", labelKey: "pix.charges.statusWaiting" },
  { id: "paid", labelKey: "pix.charges.statusPaid" },
  { id: "expired", labelKey: "pix.charges.statusExpired" },
  { id: "failed", labelKey: "pix.charges.statusFailed" },
  { id: "returned", labelKey: "pix.charges.statusReturned" },
  { id: "canceled", labelKey: "pix.charges.statusCanceled" },
];

export const PIX_CHARGE_TYPES = [
  { id: "immediate", labelKey: "pix.charges.typeImmediate" },
  { id: "with_debtor", labelKey: "pix.charges.typeWithDebtor" },
  { id: "with_due_date", labelKey: "pix.charges.typeWithDueDate" },
];

export const getPixChargeStatuses = () =>
  PIX_CHARGE_STATUSES.map(({ id, labelKey }) => ({
    id,
    label: intl.get(labelKey),
  }));

export const getPixChargeTypes = () =>
  PIX_CHARGE_TYPES.map(({ id, labelKey }) => ({
    id,
    label: intl.get(labelKey),
  }));

export const PIX_CHARGE_STATUS_META = {
  waiting: { labelKey: "pix.charges.statusWaiting", className: "bg-blue-50 text-blue-700" },
  paid: { labelKey: "pix.charges.statusPaid", className: "bg-emerald-50 text-emerald-700" },
  expired: { labelKey: "pix.charges.statusExpired", className: "bg-gray-100 text-gray-600" },
  failed: { labelKey: "pix.charges.statusFailed", className: "bg-red-50 text-red-700" },
  returned: { labelKey: "pix.charges.statusReturned", className: "bg-violet-50 text-violet-700" },
  canceled: { labelKey: "pix.charges.statusCanceled", className: "bg-red-50 text-red-700" },
};

export const PIX_CHARGE_TYPE_META = {
  immediate: { labelKey: "pix.charges.typeImmediateShort", className: "bg-amber-50 text-amber-700" },
  with_debtor: { labelKey: "pix.charges.typeWithDebtorShort", className: "bg-sky-50 text-sky-700" },
  with_due_date: { labelKey: "pix.charges.typeWithDueDateShort", className: "bg-indigo-50 text-indigo-700" },
};

export const getPixChargeStatusMeta = (status) => {
  const meta = PIX_CHARGE_STATUS_META[status];
  if (!meta) {
    return { label: status || "—", className: "bg-gray-100 text-gray-600" };
  }
  return { label: intl.get(meta.labelKey), className: meta.className };
};

export const getPixChargeTypeMeta = (type) => {
  const meta = PIX_CHARGE_TYPE_META[type];
  if (!meta) {
    return { label: type || "—", className: "bg-gray-100 text-gray-600" };
  }
  return { label: intl.get(meta.labelKey), className: meta.className };
};

const matchesSearch = (charge, query) => {
  if (!query?.trim()) return true;
  const q = query.trim().toLowerCase();
  return [charge.txid, charge.chargeId, charge.id]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
};

export const applyPixChargesClientFilters = (items = [], filters = {}) => {
  const minAmount = parseAmountInput(filters.amountMin);
  const maxAmount = parseAmountInput(filters.amountMax);
  const dateFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? endOfDay(filters.dateTo) : null;

  return items.filter((item) => {
    const createdAt = item.createdAt ? new Date(item.createdAt) : null;
    if (dateFrom && createdAt && createdAt < dateFrom) return false;
    if (dateTo && createdAt && createdAt > dateTo) return false;

    const amountBrl = (Number(item.amount) || 0) / 100;
    if (minAmount !== null && amountBrl < minAmount) return false;
    if (maxAmount !== null && amountBrl > maxAmount) return false;

    if (filters.statuses?.length && !filters.statuses.includes(item.status)) return false;
    if (filters.types?.length && !filters.types.includes(item.type)) return false;

    if (filters.txid?.trim()) {
      const txid = String(item.txid || "").toLowerCase();
      if (!txid.includes(filters.txid.trim().toLowerCase())) return false;
    }

    if (filters.chargeId?.trim()) {
      const chargeId = String(item.chargeId || item.id || "").toLowerCase();
      if (!chargeId.includes(filters.chargeId.trim().toLowerCase())) return false;
    }

    return matchesSearch(item, filters.searchQuery);
  });
};

export const isPixChargesChipFilterActive = (filters, chipId) => {
  if (!filters) return false;
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return Boolean(filters.amountMin || filters.amountMax);
    case "status":
      return (filters.statuses?.length ?? 0) > 0;
    case "type":
      return (filters.types?.length ?? 0) > 0;
    case "more":
      return Boolean(filters.txid?.trim() || filters.chargeId?.trim());
    default:
      return false;
  }
};

export const hasActivePixChargesFilters = (filters) =>
  isPixChargesChipFilterActive(filters, "date")
  || isPixChargesChipFilterActive(filters, "amount")
  || isPixChargesChipFilterActive(filters, "status")
  || isPixChargesChipFilterActive(filters, "type")
  || isPixChargesChipFilterActive(filters, "more")
  || Boolean(filters.searchQuery?.trim());

export const buildPixChargesFilterTags = (filters) => {
  const tags = [];
  const statuses = getPixChargeStatuses();
  const types = getPixChargeTypes();

  if (filters.dateFrom || filters.dateTo) {
    const from = formatFilterDate(filters.dateFrom);
    const to = formatFilterDate(filters.dateTo);
    tags.push({
      key: "date",
      iconType: "date",
      value: from && to ? `${from} – ${to}` : from || to,
    });
  }

  if (filters.amountMin || filters.amountMax) {
    const min = formatFilterAmount(filters.amountMin);
    const max = formatFilterAmount(filters.amountMax);
    tags.push({
      key: "amount",
      iconType: "amount",
      value: min && max ? `${min} – ${max}` : min || max,
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses.map((id) => labelById(statuses, id)).join(", "),
    });
  }

  if (filters.types?.length) {
    tags.push({
      key: "types",
      iconType: "type",
      value: filters.types.map((id) => labelById(types, id)).join(", "),
    });
  }

  if (filters.txid?.trim()) {
    tags.push({ key: "txid", iconType: "trace", value: filters.txid.trim() });
  }

  if (filters.chargeId?.trim()) {
    tags.push({ key: "chargeId", iconType: "external", value: filters.chargeId.trim() });
  }

  return tags;
};

export const removePixChargesFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "amount":
      return { ...filters, amountMin: "", amountMax: "" };
    case "statuses":
      return { ...filters, statuses: [] };
    case "types":
      return { ...filters, types: [] };
    case "txid":
      return { ...filters, txid: "" };
    case "chargeId":
      return { ...filters, chargeId: "" };
    default:
      return filters;
  }
};

export const togglePixChargeStatusFilter = (filters, statusId) => ({
  ...filters,
  statuses: toggleFilterListValue(filters.statuses ?? [], statusId),
});

export const togglePixChargeTypeFilter = (filters, typeId) => ({
  ...filters,
  types: toggleFilterListValue(filters.types ?? [], typeId),
});

export const exportPixChargesCsv = (items) => {
  const headers = [
    intl.get("pix.charges.txid"),
    intl.get("pix.charges.chargeId"),
    intl.get("grid.status"),
    intl.get("pix.charges.type"),
    intl.get("grid.value"),
    intl.get("grid.createdAt"),
    intl.get("pix.charges.expiresAt"),
  ];

  const rows = items.map((item) => {
    const statusMeta = getPixChargeStatusMeta(item.status);
    const typeMeta = getPixChargeTypeMeta(item.type);
    const amount = ((Number(item.amount) || 0) / 100).toFixed(2).replace(".", ",");
    return [
      item.txid || "",
      item.chargeId || item.id || "",
      statusMeta.label,
      typeMeta.label,
      amount,
      item.createdAt ? format(new Date(item.createdAt), "dd/MM/yyyy HH:mm") : "",
      item.expiresAt ? format(new Date(item.expiresAt), "dd/MM/yyyy HH:mm") : "",
    ];
  });

  downloadCsv(headers, rows, `cobrancas-pix-${format(new Date(), "yyyy-MM-dd")}.csv`);
};
